import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
} from "@mui/material";
import { Close, Remove } from "@mui/icons-material";
import { format } from "date-fns";
import { Transaction } from "../../types/Transaction";
import { useAccelContext } from "../../contexts/AccelContext/AccelContext";

interface DownloadPopupProps {
  open: boolean;
  onClose: () => void;
  transactions: Transaction[];
}

const DownloadPopup: React.FC<DownloadPopupProps> = ({
  open,
  onClose,
  transactions,
}) => {
  const [exportFormat, setExportFormat] = useState("xlsx");
  const { theme } = useAccelContext();

  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExportFormat(event.target.value);
  };

  const handleDownload = () => {
    const headers = [
      "Date",
      "Vendor",
      "Description",
      "Amount",
      "Category",
      "Account",
      "Status",
    ];
    const csvContent = [
      headers.join(","),
      ...transactions.map((transaction) =>
        [
          format(new Date(transaction.date), "yyyy-MM-dd"),
          transaction.counterparty_name,
          transaction.description,
          transaction.amount,
          transaction.status,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = "transactions.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: theme?.accent?.hex || "#000000",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          bgcolor: theme?.primary?.hex || "#000000",
          color: theme?.text?.hex || "#ffffff",
        }}
      >
        <Typography variant="h6">Custom Report</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme?.text?.hex || "#ffffff",
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          bgcolor: theme?.accent?.hex || "#1e1e1e",
          color: theme?.text?.hex || "#ffffff",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Select export format
              </Typography>
              <RadioGroup
                row
                value={exportFormat}
                onChange={handleFormatChange}
              >
                <FormControlLabel
                  value="csv"
                  control={
                    <Radio sx={{ color: theme?.secondary?.hex || "#ffffff" }} />
                  }
                  label={
                    <Typography sx={{ color: theme?.text?.hex || "#ffffff" }}>
                      CSV
                    </Typography>
                  }
                />
              </RadioGroup>
            </div>
            <Button
              variant="contained"
              onClick={handleDownload}
              sx={{
                bgcolor: theme?.primary?.hex || "orange",
                color: theme?.text?.hex || "black",
                "&:hover": { bgcolor: theme?.secondary?.hex || "darkorange" },
              }}
            >
              Download
            </Button>
          </div>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: theme?.text?.hex || "#ffffff" }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ color: theme?.text?.hex || "#ffffff" }}>
                    Vendor
                  </TableCell>
                  <TableCell sx={{ color: theme?.text?.hex || "#ffffff" }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ color: theme?.text?.hex || "#ffffff" }}>
                    Amount
                  </TableCell>
                  <TableCell sx={{ color: theme?.text?.hex || "#ffffff" }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ color: theme?.text?.hex || "#ffffff" }}>
                    Account
                  </TableCell>
                  <TableCell sx={{ color: theme?.text?.hex || "#ffffff" }}>
                    Status
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "& td": {
                        border: `1px solid ${theme?.secondary?.hex || "#333"}`,
                        color: theme?.text?.hex || "#ffffff",
                      },
                    }}
                  >
                    <TableCell>
                      {format(new Date(transaction.date), "MMM do")}
                    </TableCell>
                    <TableCell>{transaction.counterparty_name}</TableCell>
                    <TableCell>{transaction.description}</TableCell>
                    <TableCell>{transaction.amount}</TableCell>

                    <TableCell>{transaction.status}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        sx={{ color: theme?.secondary?.hex || "#ffffff" }}
                      >
                        <Remove />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadPopup;
